/**
 * playSession.ts — wind-down timer for the child's *active* play time.
 *
 * Counts only ACTIVE play time (page visible AND tab in foreground), not
 * wall-clock time since "first interaction". So a kid who plays 10 minutes,
 * closes the iPad to eat lunch for an hour, then re-opens still sees the
 * accumulator at 10 minutes — not 70. The wind-down threshold is parent-
 * configurable; once crossed, isSleepyNow() returns true and the rest of
 * the app (gameAudio sleepy override + SleepOverlay banner) acts on it.
 *
 * 调用关系：
 *   - lib/gameAudio.ts playPetVoice() — forces mood→'sleepy' when isSleepyNow()
 *   - components/SleepOverlay.tsx — usePlaySession() hook for the soft nudge
 *   - pages/ParkPage.tsx — flips scope on/off via setPlayScopeActive(true)
 *     when entering/leaving the playground tab (v0.14.0 — playtime is now
 *     scoped to ParkPage; chatting on /chat no longer counts).
 *   - admin/* (parent admin) writes setSleepThresholdMinutes via api.* sync
 *
 * 关键约束：
 *   - Browser-only (uses localStorage + visibilityState + setInterval).
 *   - Only ParkPage flips scopeActive on. Other routes won't accumulate
 *     even if usePlaySession is mounted somewhere.
 *   - Storage layout (localStorage):
 *       xiaopeiban_play_session_start  — wall-clock anchor for the period
 *                                        (used purely to detect rollover,
 *                                        NOT for the elapsed calculation).
 *       xiaopeiban_play_accumulated_ms — total active time within the
 *                                        current period, in milliseconds.
 *       xiaopeiban_play_last_tick_at   — wall-clock of the last tick that
 *                                        counted toward the accumulator
 *                                        (used to compute next delta safely).
 *   - Reset rule: once the configured period elapses (rolling N hours OR
 *     per calendar day, controlled by setPlayPeriod), all three keys clear.
 *   - setSession() in api/client.ts resets these keys on account switch.
 */
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'xiaopeiban_play_session_start';
const ACCUM_KEY = 'xiaopeiban_play_accumulated_ms';
const LAST_TICK_KEY = 'xiaopeiban_play_last_tick_at';
const THRESHOLD_KEY = 'xiaopeiban_sleep_threshold_minutes';
const PERIOD_TYPE_KEY = 'xiaopeiban_play_period_type';   // 'hours' | 'day'
const PERIOD_HOURS_KEY = 'xiaopeiban_play_period_hours'; // 1..24
/** Default before parent configures: 15 active minutes triggers wind-down. */
export const DEFAULT_SLEEP_THRESHOLD_MINUTES = 15;
/** Default play-period type: rolling 'hours' window (vs strict calendar 'day'). */
export const DEFAULT_PLAY_PERIOD_TYPE: 'hours' | 'day' = 'hours';
/** Default rolling window length (hours) when periodType === 'hours'. */
export const DEFAULT_PLAY_PERIOD_HOURS = 12;
/** Polling cadence — usePlaySession ticks every 15s while a session runs. */
const TICK_MS = 15 * 1000;
// Cap a single tick's accumulator delta. Protects against runaway
// browser timer drift (e.g. computer slept 8h, suddenly fires)
// — even with visibility checks in place we don't want one rogue
// tick to add an hour.
const MAX_TICK_DELTA_MS = 30 * 1000;

let activeThresholdMinutes = readStoredThreshold();
let activePeriodType: 'hours' | 'day' = readStoredPeriodType();
let activePeriodHours = readStoredPeriodHours();

// v0.14.0 — scope-gated accumulation; only ParkPage flips this on.
let scopeActive = false;

function readStoredThreshold(): number {
  if (typeof window === 'undefined') return DEFAULT_SLEEP_THRESHOLD_MINUTES;
  const raw = window.localStorage.getItem(THRESHOLD_KEY);
  const n = raw ? Number(raw) : NaN;
  if (Number.isFinite(n) && n >= 1 && n <= 720) return Math.round(n);
  return DEFAULT_SLEEP_THRESHOLD_MINUTES;
}
function readStoredPeriodType(): 'hours' | 'day' {
  if (typeof window === 'undefined') return DEFAULT_PLAY_PERIOD_TYPE;
  const raw = window.localStorage.getItem(PERIOD_TYPE_KEY);
  return raw === 'day' || raw === 'hours' ? raw : DEFAULT_PLAY_PERIOD_TYPE;
}
function readStoredPeriodHours(): number {
  if (typeof window === 'undefined') return DEFAULT_PLAY_PERIOD_HOURS;
  const raw = window.localStorage.getItem(PERIOD_HOURS_KEY);
  const n = raw ? Number(raw) : NaN;
  if (Number.isFinite(n) && n >= 1 && n <= 24) return Math.round(n);
  return DEFAULT_PLAY_PERIOD_HOURS;
}

export function getSleepThresholdMinutes(): number { return activeThresholdMinutes; }
export function getPlayPeriodType(): 'hours' | 'day' { return activePeriodType; }
export function getPlayPeriodHours(): number { return activePeriodHours; }

/** Apply a parent-configured threshold (called after fetching from API). */
export function setSleepThresholdMinutes(min: number): void {
  if (!Number.isFinite(min) || min < 1 || min > 720) return;
  const rounded = Math.round(min);
  if (rounded === activeThresholdMinutes) return;
  activeThresholdMinutes = rounded;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(THRESHOLD_KEY, String(rounded));
    window.dispatchEvent(new CustomEvent('xiaopeiban:play-threshold-changed'));
  }
}

/** v0.14.0 — entering ParkPage flips this on; leaving flips it off. */
export function setPlayScopeActive(active: boolean): void {
  if (scopeActive === active) return;
  scopeActive = active;
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LAST_TICK_KEY);
  window.dispatchEvent(new CustomEvent('xiaopeiban:play-threshold-changed'));
}

/** Apply parent-configured period (rolling N hours vs strict calendar day). */
export function setPlayPeriod(type: 'hours' | 'day', hours: number): void {
  const t = type === 'day' ? 'day' : 'hours';
  const h = Number.isFinite(hours) && hours >= 1 && hours <= 24
    ? Math.round(hours)
    : DEFAULT_PLAY_PERIOD_HOURS;
  if (t === activePeriodType && h === activePeriodHours) return;
  activePeriodType = t;
  activePeriodHours = h;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(PERIOD_TYPE_KEY, t);
    window.localStorage.setItem(PERIOD_HOURS_KEY, String(h));
    window.dispatchEvent(new CustomEvent('xiaopeiban:play-threshold-changed'));
  }
}

function thresholdMs(): number { return activeThresholdMinutes * 60 * 1000; }

/**
 * Has the configured play-period elapsed since `start`? Two modes:
 *   • 'hours' — rolling: now − start ≥ N hours.
 *   • 'day'   — strict: start's local calendar date < today's local date.
 */
function periodElapsedSince(startMs: number, nowMs = Date.now()): boolean {
  if (activePeriodType === 'day') {
    const a = new Date(startMs);
    const b = new Date(nowMs);
    return a.getFullYear() !== b.getFullYear()
      || a.getMonth() !== b.getMonth()
      || a.getDate() !== b.getDate();
  }
  const windowMs = activePeriodHours * 60 * 60 * 1000;
  return nowMs - startMs >= windowMs;
}

function isPageVisible(): boolean {
  if (typeof document === 'undefined') return true;
  // 'visible' covers the in-foreground PWA / browser tab. 'hidden' covers
  // backgrounded, locked screen, switched-away apps. We DON'T accumulate
  // play time while hidden.
  return document.visibilityState === 'visible';
}

/**
 * Returns the wall-clock anchor of the current period, or null if we
 * haven't started one (or it just rolled over). Side-effect: clears all
 * play-session keys when the period rolls over.
 */
export function getSessionStart(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const ts = Number(raw);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  if (periodElapsedSince(ts)) {
    resetPlaySession();
    return null;
  }
  return ts;
}

/**
 * Bump the active-play accumulator by the time elapsed since the previous
 * tick, but only when the page is actually visible. Called both by the
 * setInterval tick and on demand (e.g. when markPlayActivity fires).
 *
 * Behaviour highlights:
 *  - When the page is hidden, we DROP the last_tick anchor so the next
 *    visible tick starts fresh from "one TICK_MS ago" rather than claiming
 *    the entire hidden window.
 *  - Per-tick growth is capped at MAX_TICK_DELTA_MS (30s).
 */
function applyVisibleTick(): boolean {
  if (typeof window === 'undefined') return false;
  // v0.14.0 — only scoped routes accumulate (currently ParkPage).
  if (!scopeActive) {
    window.localStorage.removeItem(LAST_TICK_KEY);
    return false;
  }
  if (!isPageVisible()) {
    // Drop the anchor — when we come back visible, treat it as a fresh tick.
    window.localStorage.removeItem(LAST_TICK_KEY);
    return false;
  }
  const now = Date.now();
  const lastTick = Number(window.localStorage.getItem(LAST_TICK_KEY) || '0');
  const delta = lastTick > 0
    ? Math.min(Math.max(0, now - lastTick), MAX_TICK_DELTA_MS)
    : TICK_MS; // first tick after becoming visible: count one interval
  if (delta > 0) {
    const cur = Number(window.localStorage.getItem(ACCUM_KEY) || '0');
    window.localStorage.setItem(ACCUM_KEY, String(cur + delta));
  }
  window.localStorage.setItem(LAST_TICK_KEY, String(now));
  return delta > 0;
}

/** Call from any pet interaction or chat send. */
export function markPlayActivity(): void {
  if (typeof window === 'undefined') return;
  // Anchor the period if we don't have one yet.
  if (!getSessionStart()) {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    window.dispatchEvent(new CustomEvent('xiaopeiban:play-session-started'));
  }
  // Try to advance the accumulator immediately so the UI can react now.
  applyVisibleTick();
}

export function resetPlaySession(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(ACCUM_KEY);
  window.localStorage.removeItem(LAST_TICK_KEY);
  window.dispatchEvent(new CustomEvent('xiaopeiban:play-session-reset'));
}

export function getElapsedMs(): number {
  // Touch getSessionStart so a stale period gets detected + reset.
  if (!getSessionStart()) return 0;
  if (typeof window === 'undefined') return 0;
  const cur = Number(window.localStorage.getItem(ACCUM_KEY) || '0');
  return Number.isFinite(cur) && cur > 0 ? cur : 0;
}

export function isSleepyNow(): boolean {
  return getElapsedMs() >= thresholdMs();
}

export interface PlaySessionState {
  start: number | null;
  elapsedMs: number;
  isSleepy: boolean;
  minutesLeft: number; // negative once we're past threshold
}

/**
 * React hook — re-renders every TICK_MS while a session is running so the
 * sleepy-mode UI flips on at the right moment without anyone having to
 * wire setInterval themselves. Each tick also advances the active-play
 * accumulator if the page is visible.
 */
export function usePlaySession(): PlaySessionState {
  const [state, setState] = useState<PlaySessionState>(() => snapshot());

  useEffect(() => {
    const tick = () => {
      // Only advance if a period anchor exists (i.e. the kid actually
      // started playing). Otherwise we just check for stale anchors.
      if (getSessionStart()) applyVisibleTick();
      setState(snapshot());
    };
    tick();
    const id = window.setInterval(tick, TICK_MS);
    const onStart = () => tick();
    const onReset = () => tick();
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === ACCUM_KEY) tick();
    };
    const onThreshold = () => tick();
    const onVisibility = () => {
      // When the tab becomes hidden, drop the tick anchor immediately so
      // the next visible tick doesn't claim the hidden interval.
      if (!isPageVisible() && typeof window !== 'undefined') {
        window.localStorage.removeItem(LAST_TICK_KEY);
      }
      tick();
    };
    window.addEventListener('xiaopeiban:play-session-started', onStart);
    window.addEventListener('xiaopeiban:play-session-reset', onReset);
    window.addEventListener('xiaopeiban:play-threshold-changed', onThreshold);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('xiaopeiban:play-session-started', onStart);
      window.removeEventListener('xiaopeiban:play-session-reset', onReset);
      window.removeEventListener('xiaopeiban:play-threshold-changed', onThreshold);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return state;
}

function snapshot(): PlaySessionState {
  const start = getSessionStart();
  const elapsedMs = start ? getElapsedMs() : 0;
  const ms = thresholdMs();
  return {
    start,
    elapsedMs,
    isSleepy: elapsedMs >= ms,
    minutesLeft: Math.ceil((ms - elapsedMs) / 60_000),
  };
}
